import Dexie, { type Table } from 'dexie';
import type { TodoRecurrence, TodoLogEntry } from '@familyhub/shared';
import { registerCleanup } from '../platform/cleanup';
import type { OutboxEntry, MetaRow, RejectionRow } from '../platform/sync-runner';

/**
 * The Todo module's local database — a **separate** IndexedDB database from
 * groceries (`familyhub`) and agenda (`familyhub-todos` vs `familyhub-agenda`),
 * which is the module-independence boundary on the client. Its table, outbox and
 * cursor never touch another module's store.
 *
 * Rows are stored exactly as the server sends them, tombstones (`deleted_at`)
 * included and filtered at read time — a tombstone is what propagates a delete
 * to the other phone.
 */

export interface TodoRecord {
  id: string;
  title: string;
  done: boolean;
  assigned_to: string | null;
  scheduled_at: string | null;
  recurrence: TodoRecurrence | null;
  category: string | null;
  log: TodoLogEntry[] | null;
  position: number;
  created_by: string | null;
  created_at?: string;
  updated_at: string;
  deleted_at: string | null;
}

export class TodoDb extends Dexie {
  todos!: Table<TodoRecord, string>;
  outbox!: Table<OutboxEntry, number>;
  rejections!: Table<RejectionRow, string>;
  meta!: Table<MetaRow, string>;

  constructor() {
    super('familyhub-todos');
    this.version(1).stores({
      todos: 'id, position, done, scheduled_at, deleted_at',
      outbox: '++seq, queued_at',
      rejections: 'id, at',
      meta: 'key',
    });
  }
}

export const tdb = new TodoDb();

/** The Dexie tables the sync runner mirrors, keyed by the server's change bucket. */
export const TODO_TABLE_MAP = [
  { name: 'todos', table: tdb.todos as unknown as Table<Record<string, unknown>, string> },
];

/** Wipe on logout — registered with the platform cleanup registry. */
export async function clearTodoData(): Promise<void> {
  await Promise.all([tdb.todos.clear(), tdb.outbox.clear(), tdb.rejections.clear(), tdb.meta.clear()]);
}

registerCleanup(clearTodoData);
